/**
 * CompatibilityPage — Kundali Matching (कुंडली मिलान)
 *
 * Calculates Ashtakoot Guna Milan between the logged-in user and a partner.
 * Displays all 8 kuta scores, doshas, and the overall compatibility tier.
 *
 */
import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  compatibilityApi,
  type CompatibilityPartner,
  type AshtakootScore,
  type CompatibilityResult,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import { useFeatureGate } from '@/hooks/useSubscription'
import { HardGate } from '@/components/paywall'
import {
  Heart,
  User,
  Users,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MapPin,
  Calendar,
  Clock,
  Sparkles,
} from 'lucide-react'

// ── Kuta definitions ──────────────────────────────────────────────────────

const KUTAS = [
  {
    key: 'varna' as const,
    max: 1,
    label: 'Varna',
    sanskrit: 'वर्ण',
    desc: 'Spiritual compatibility & evolution',
  },
  {
    key: 'vashya' as const,
    max: 2,
    label: 'Vashya',
    sanskrit: 'वश्य',
    desc: 'Mutual influence & rapport',
  },
  {
    key: 'tara' as const,
    max: 3,
    label: 'Tara',
    sanskrit: 'तारा',
    desc: 'Birth star harmony & wellbeing',
  },
  {
    key: 'yoni' as const,
    max: 4,
    label: 'Yoni',
    sanskrit: 'योनि',
    desc: 'Physical & intimate compatibility',
  },
  {
    key: 'grahaMaitri' as const,
    max: 5,
    label: 'Graha Maitri',
    sanskrit: 'ग्रह मैत्री',
    desc: 'Planetary friendship & mental bond',
  },
  {
    key: 'gana' as const,
    max: 6,
    label: 'Gana',
    sanskrit: 'गण',
    desc: 'Temperament & nature compatibility',
  },
  {
    key: 'bhakoot' as const,
    max: 7,
    label: 'Bhakoot',
    sanskrit: 'भकूट',
    desc: 'Emotional & financial harmony',
  },
  {
    key: 'nadi' as const,
    max: 8,
    label: 'Nadi',
    sanskrit: 'नाड़ी',
    desc: 'Physical health & progeny',
  },
]

// ── Tier config ───────────────────────────────────────────────────────────

const TIER_CONFIG = {
  excellent: {
    label: 'Excellent Match',
    range: '32–36',
    textColor: 'text-auspicious',
    ringColor: '#22c55e',
    bgBadge: 'bg-auspicious/10 border-auspicious/30 text-auspicious',
  },
  good: {
    label: 'Good Match',
    range: '25–31',
    textColor: 'text-gold',
    ringColor: '#f59e0b',
    bgBadge: 'bg-gold/10 border-gold/30 text-gold',
  },
  average: {
    label: 'Average Match',
    range: '18–24',
    textColor: 'text-star-soft',
    ringColor: '#9090c0',
    bgBadge: 'bg-cosmic-dusk border-cosmic-mist text-star-soft',
  },
  poor: {
    label: 'Challenging Match',
    range: '0–17',
    textColor: 'text-inauspicious',
    ringColor: '#ef4444',
    bgBadge: 'bg-inauspicious/10 border-inauspicious/30 text-inauspicious',
  },
} as const

// ── Score ring (SVG) ──────────────────────────────────────────────────────

function ScoreRing({ score, tier }: { score: number; tier: AshtakootScore['compatibility'] }) {
  const size = 160
  const sw = 12
  const r = (size - sw * 2) / 2
  const circumference = 2 * Math.PI * r
  const fraction = 0.75
  const dashTotal = circumference * fraction
  const offset = dashTotal * (1 - score / 36)
  const color = TIER_CONFIG[tier].ringColor
  const pct = Math.round((score / 36) * 100)

  return (
    <div className="flex flex-col items-center gap-2">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
        {/* Track */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" style={{ stroke: 'var(--chart-border)' }} strokeWidth={sw}
          strokeDasharray={`${dashTotal} ${circumference - dashTotal}`}
          strokeLinecap="round"
          transform={`rotate(135 ${size / 2} ${size / 2})`}
        />
        {/* Fill */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color} strokeWidth={sw}
          strokeDasharray={`${dashTotal - offset} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(135 ${size / 2} ${size / 2})`}
          style={{ filter: `drop-shadow(0 0 8px ${color}60)`, transition: 'stroke-dasharray 1s ease-out' }}
        />
        {/* Score */}
        <text x={size / 2} y={size / 2 - 8} textAnchor="middle"
          fill={color} fontSize={32} fontWeight={700} fontFamily="Geist, sans-serif">
          {score}
        </text>
        <text x={size / 2} y={size / 2 + 12} textAnchor="middle"
          style={{ fill: 'var(--chart-text-muted)' }} fontSize={11} fontFamily="Geist, sans-serif">
          out of 36
        </text>
        <text x={size / 2} y={size / 2 + 28} textAnchor="middle"
          style={{ fill: 'var(--chart-text-dim)' }} fontSize={10} fontFamily="Geist Mono, monospace">
          {pct}%
        </text>
      </svg>
      <span className={cn('text-sm font-semibold', TIER_CONFIG[tier].textColor)}>
        {TIER_CONFIG[tier].label}
      </span>
    </div>
  )
}

// ── Kuta bar ──────────────────────────────────────────────────────────────

function KutaBar({ points, max }: { points: number; max: number }) {
  const pct = (points / max) * 100
  const color = points === max ? '#22c55e' : points >= max * 0.6 ? '#f59e0b' : points > 0 ? '#9090c0' : '#ef4444'

  return (
    <div className="flex items-center gap-2">
      <div className="relative h-1.5 flex-1 rounded-full bg-cosmic-mist/30 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color, boxShadow: `0 0 6px ${color}60` }}
        />
      </div>
      <span className="w-10 text-right font-mono text-xs text-star-soft">
        {points}/{max}
      </span>
    </div>
  )
}

// ── Dosha badge ───────────────────────────────────────────────────────────

function DoshaBadge({ name, sanskrit, present }: { name: string; sanskrit: string; present: boolean }) {
  return (
    <div className={cn(
      'flex items-center gap-2 rounded-lg px-3 py-2 border text-sm',
      present
        ? 'bg-inauspicious/10 border-inauspicious/30 text-inauspicious'
        : 'bg-auspicious/5 border-auspicious/20 text-auspicious',
    )}>
      {present
        ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        : <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />}
      <div className="flex flex-col">
        <span className="font-medium leading-none">{name} Dosha</span>
        <span className="text-2xs font-display mt-0.5 opacity-70">{sanskrit}</span>
      </div>
    </div>
  )
}

// ── Nominatim geocoding ──────────────────────────────────────────────────

interface NominatimResult {
  lat: string
  lon: string
  display_name: string
  address?: { city?: string; town?: string; village?: string; state?: string; country?: string }
}

async function searchNominatim(query: string): Promise<NominatimResult[]> {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5&featuretype=city`
  const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
  if (!res.ok) return []
  return res.json() as Promise<NominatimResult[]>
}

function nominatimLabel(r: NominatimResult): string {
  const a = r.address
  if (!a) return r.display_name
  const city = a.city || a.town || a.village || ''
  const parts = [city, a.state, a.country].filter(Boolean)
  return parts.join(', ') || r.display_name
}

// ── Partner form ──────────────────────────────────────────────────────────

interface PartnerFormData {
  name:       string
  birthDate:  string
  birthTime:  string
  birthPlace: string
  latitude:   string
  longitude:  string
}

const EMPTY_FORM: PartnerFormData = {
  name:       '',
  birthDate:  '',
  birthTime:  '',
  birthPlace: '',
  latitude:   '',
  longitude:  '',
}

function PartnerForm({
  data,
  onChange,
  onSubmit,
  loading,
}: {
  data: PartnerFormData
  onChange: (d: PartnerFormData) => void
  onSubmit: () => void
  loading: boolean
}) {
  const set = (field: keyof PartnerFormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...data, [field]: e.target.value })

  // City search state
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handlePlaceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    onChange({ ...data, birthPlace: val, latitude: '', longitude: '' })
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (val.length >= 3) {
      debounceRef.current = setTimeout(async () => {
        const results = await searchNominatim(val)
        setSuggestions(results)
        setShowSuggestions(results.length > 0)
      }, 400)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
  }, [data, onChange])

  const handleSuggestionClick = useCallback((r: NominatimResult) => {
    onChange({
      ...data,
      birthPlace: nominatimLabel(r),
      latitude: parseFloat(r.lat).toFixed(4),
      longitude: parseFloat(r.lon).toFixed(4),
    })
    setShowSuggestions(false)
    setSuggestions([])
  }, [data, onChange])

  // Close suggestions on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const isValid = data.name && data.birthDate && data.birthTime && data.birthPlace &&
    data.latitude && data.longitude

  return (
    <div className="card-cosmic rounded-xl p-5 flex flex-col gap-4">
      <div className="flex items-center gap-2 mb-1">
        <User className="h-4 w-4 text-gold" />
        <h2 className="text-sm font-semibold text-star-bright">Partner Details</h2>
        <span className="text-2xs font-display text-star-muted ml-auto">साथी विवरण</span>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {/* Name */}
        <div>
          <label className="block text-2xs text-star-muted mb-1 font-mono uppercase tracking-wider">Full Name</label>
          <input
            type="text"
            placeholder="Partner's name"
            value={data.name}
            onChange={set('name')}
            className="input-cosmic w-full"
          />
        </div>

        {/* Date & Time row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-2xs text-star-muted mb-1 font-mono uppercase tracking-wider">
              <Calendar className="inline h-3 w-3 mr-1" />Birth Date
            </label>
            <input
              type="date"
              value={data.birthDate}
              onChange={set('birthDate')}
              className="input-cosmic w-full"
            />
          </div>
          <div>
            <label className="block text-2xs text-star-muted mb-1 font-mono uppercase tracking-wider">
              <Clock className="inline h-3 w-3 mr-1" />Birth Time
            </label>
            <input
              type="time"
              value={data.birthTime}
              onChange={set('birthTime')}
              className="input-cosmic w-full"
            />
          </div>
        </div>

        {/* Place with autocomplete */}
        <div ref={wrapperRef} className="relative">
          <label className="block text-2xs text-star-muted mb-1 font-mono uppercase tracking-wider">
            <MapPin className="inline h-3 w-3 mr-1" />Birth Place
          </label>
          <input
            type="text"
            placeholder="Type a city name…"
            value={data.birthPlace}
            onChange={handlePlaceChange}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            className="input-cosmic w-full"
          />
          {showSuggestions && (
            <ul className="absolute z-20 mt-1 w-full rounded-lg border border-cosmic-mist/30 bg-cosmic-void/95 backdrop-blur-sm shadow-lg max-h-48 overflow-y-auto">
              {suggestions.map((r, i) => (
                <li
                  key={i}
                  onClick={() => handleSuggestionClick(r)}
                  className="px-3 py-2 text-sm text-star-soft hover:bg-cosmic-mist/20 cursor-pointer border-b border-cosmic-mist/10 last:border-0"
                >
                  {nominatimLabel(r)}
                </li>
              ))}
            </ul>
          )}
          {data.latitude && data.longitude && (
            <p className="mt-1 text-2xs text-star-dim font-mono">
              {data.latitude}°N, {data.longitude}°E
            </p>
          )}
        </div>
      </div>

      <button
        disabled={!isValid || loading}
        onClick={onSubmit}
        className={cn(
          'mt-1 flex items-center justify-center gap-2 rounded-lg py-2.5 px-4 text-sm font-semibold transition-all',
          isValid && !loading
            ? 'bg-gold text-cosmic-void hover:bg-gold-bright shadow-glow-gold'
            : 'bg-cosmic-mist/20 text-star-dim cursor-not-allowed',
        )}
      >
        {loading ? (
          <><Loader2 className="h-4 w-4 animate-spin" />Calculating…</>
        ) : (
          <><Sparkles className="h-4 w-4" />Calculate Compatibility</>
        )}
      </button>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export function CompatibilityPage() {
  const gate = useFeatureGate('compatibility')
  const [form, setForm] = useState<PartnerFormData>(EMPTY_FORM)
  const [result, setResult] = useState<CompatibilityResult | null>(null)

  const mutation = useMutation({
    mutationFn: (partner: CompatibilityPartner) => compatibilityApi.calculate(partner),
    onSuccess: (data) => {
      setResult(data)
    },
  })

  const handleCalculate = () => {
    const partner: CompatibilityPartner = {
      name:       form.name,
      birthDate:  form.birthDate,
      birthTime:  form.birthTime,
      birthPlace: form.birthPlace,
      latitude:   parseFloat(form.latitude),
      longitude:  parseFloat(form.longitude),
    }
    mutation.mutate(partner)
  }

  const score = result?.score ?? null

  const totalMax = useMemo(() =>
    KUTAS.reduce((sum, k) => sum + k.max, 0),
    [],
  )

  return (
    <div className="flex flex-col gap-6 pb-8 animate-[fade-in_0.4s_ease-out]">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-gold" />
            <h1 className="text-xl font-bold text-star-bright">Kundali Matching</h1>
          </div>
          <p className="mt-1 text-sm text-star-muted font-display italic">
            कुंडली मिलान — Ashtakoot Guna Milan
          </p>
        </div>
        {score && (
          <span className={cn(
            'rounded-full px-3 py-1 text-xs font-semibold border',
            TIER_CONFIG[score.compatibility].bgBadge,
          )}>
            {TIER_CONFIG[score.compatibility].label}
          </span>
        )}
      </div>

      {mutation.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-inauspicious/20 bg-inauspicious/10 px-4 py-2.5 text-xs text-inauspicious">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Could not calculate compatibility. Please check your input and try again.
        </div>
      )}

      <HardGate locked={!gate.allowed} featureName="Kundali Matching">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[360px_1fr]">
        {/* LEFT — Partner form */}
        <PartnerForm
          data={form}
          onChange={setForm}
          onSubmit={handleCalculate}
          loading={mutation.isPending}
        />

        {/* RIGHT — Results */}
        {score ? (
          <div className="flex flex-col gap-5">
            {/* People header — side-by-side comparison */}
            <div className="card-cosmic rounded-xl p-4">
              <div className="flex items-center justify-between gap-2">
                {/* Person A */}
                <div className="flex-1 min-w-0 p-3 rounded-xl bg-cosmic-dusk/60 border border-cosmic-mist">
                  <PersonChip
                    name={result!.person.name}
                    place={result!.person.birthPlace}
                    date={result!.person.birthDate}
                    icon={<User className="h-3.5 w-3.5" />}
                  />
                </div>
                {/* Connector */}
                <div className="flex-shrink-0 flex flex-col items-center gap-1">
                  <div className="w-px h-4 bg-gradient-to-b from-transparent to-gold/30" />
                  <div className="w-8 h-8 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <Heart className="h-4 w-4 text-gold" />
                  </div>
                  <div className="w-px h-4 bg-gradient-to-t from-transparent to-gold/30" />
                </div>
                {/* Partner */}
                <div className="flex-1 min-w-0 p-3 rounded-xl bg-cosmic-dusk/60 border border-cosmic-mist">
                  <PersonChip
                    name={result!.partner.name}
                    place={result!.partner.birthPlace}
                    date={result!.partner.birthDate}
                    icon={<Users className="h-3.5 w-3.5" />}
                    align="right"
                  />
                </div>
              </div>
            </div>

            {/* Score ring + doshas */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Ring */}
              <div className="card-cosmic rounded-xl p-5 flex flex-col items-center gap-4">
                <ScoreRing score={score.totalPoints} tier={score.compatibility} />
                <div className="text-center">
                  <p className="text-xs text-star-muted">
                    Total Guna Points
                  </p>
                  <p className="text-2xs font-display italic text-star-dim mt-0.5">
                    गुण मिलान — {score.totalPoints} / {totalMax}
                  </p>
                </div>
              </div>

              {/* Doshas */}
              <div className="card-cosmic rounded-xl p-5 flex flex-col gap-3">
                <h3 className="text-xs font-semibold text-star-muted uppercase tracking-wider font-mono">Dosha Check</h3>
                <DoshaBadge name="Nadi"    sanskrit="नाड़ी दोष"  present={score.nadiDosha}    />
                <DoshaBadge name="Bhakoot" sanskrit="भकूट दोष"  present={score.bhakootDosha} />
                <DoshaBadge name="Gana"    sanskrit="गण दोष"    present={score.ganaDosha}    />
                {!score.nadiDosha && !score.bhakootDosha && !score.ganaDosha && (
                  <p className="text-xs text-auspicious/70 mt-1 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    No major doshas detected
                  </p>
                )}
              </div>
            </div>

            {/* 8 Kutas breakdown */}
            <div className="card-cosmic rounded-xl p-5">
              <h3 className="text-xs font-semibold text-star-muted uppercase tracking-wider font-mono mb-4">
                Ashtakoot Breakdown — 8 Kutas
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {KUTAS.map((kuta) => {
                  const pts = score.breakdown[kuta.key]
                  const pctFull = pts === kuta.max
                  return (
                    <div key={kuta.key} className="flex flex-col gap-1.5">
                      <div className="flex items-baseline justify-between">
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-sm font-medium text-star">{kuta.label}</span>
                          <span className="text-2xs font-display text-star-dim">{kuta.sanskrit}</span>
                        </div>
                        {pctFull && (
                          <span className="text-2xs text-auspicious font-mono">✓ full</span>
                        )}
                      </div>
                      <KutaBar points={pts} max={kuta.max} />
                      <p className="text-2xs text-star-dim leading-tight">{kuta.desc}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          <EmptyState loading={mutation.isPending} />
        )}
      </div>
      </HardGate>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────

function PersonChip({
  name, place, date, icon, align = 'left',
}: {
  name: string; place: string; date: string; icon: React.ReactNode; align?: 'left' | 'right'
}) {
  return (
    <div className={cn('flex flex-col gap-0.5', align === 'right' && 'items-end text-right')}>
      <div className={cn('flex items-center gap-1.5 text-star-bright', align === 'right' && 'flex-row-reverse')}>
        <span className="text-gold">{icon}</span>
        <span className="text-sm font-semibold">{name}</span>
      </div>
      <span className="text-2xs text-star-muted font-mono">{date}</span>
      <span className="text-2xs text-star-dim flex items-center gap-1">
        <MapPin className="h-2.5 w-2.5 inline" />
        {place}
      </span>
    </div>
  )
}

function EmptyState({ loading }: { loading: boolean }) {
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="card-cosmic p-6 space-y-3">
          <div className="shimmer h-4 w-2/5 rounded-md" />
          <div className="shimmer h-3 w-3/5 rounded-md" />
          <div className="grid grid-cols-4 gap-3 mt-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="shimmer h-16 rounded-xl" />
            ))}
          </div>
        </div>
        <div className="shimmer h-32 rounded-2xl" />
      </div>
    )
  }
  return (
    <div className="card-cosmic rounded-xl flex flex-col items-center justify-center gap-3 p-12 text-star-muted">
      <Heart className="h-10 w-10 text-cosmic-mist/40" />
      <p className="text-sm text-center">Enter your partner's birth details<br />and calculate compatibility</p>
      <p className="text-2xs font-display italic text-star-dim">अष्टकूट गुण मिलान</p>
    </div>
  )
}
